import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import { useEffect, useMemo, useRef } from 'react'
import {
  CartesianGrid,
  Line,
  LabelList,
  LabelProps,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'
import { PatientRecordedDataType } from '../../state/recordedData/types'

type RecordedDataLineChartProp = {
  lineChartDataKey?: 'spo2' | 'pr' | 'resp' | 'temp' | 'dia' | 'sys' | 'mews'
  lineChartDataName: string
  recordedData: PatientRecordedDataType[] | undefined
  startTime: number
  endTime: number
  multiLineChartKey?: (
    | 'spo2'
    | 'pr'
    | 'resp'
    | 'temp'
    | 'dia'
    | 'sys'
    | 'mews'
  )[]
}

const PrintChartContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
`

const PrintChartTitle = styled.div`
  font-size: 1rem;
  font-weight: 800;
  padding-left: 1rem;
  min-width: 10rem;
  text-align: center;
`

const PrintChartWrapper = styled.div`
  width: 100%;
`

export default function PrintRecordedDataChart({
  lineChartDataKey,
  lineChartDataName,
  recordedData,
  endTime,
  startTime,
  multiLineChartKey,
}: RecordedDataLineChartProp) {
  const chartRef = useRef<HTMLDivElement | null>(null)

  const chartData = useMemo(() => {
    return recordedData
      ? recordedData.map((data, index) => {
        let nextIndex = index + 1 < recordedData.length ? index + 1 : index
        if (lineChartDataKey) {
          let value = valueManipulation(
            `${data[lineChartDataKey]}`,
            lineChartDataKey
          )
          return {
            [`${lineChartDataKey}-${getScore(data, lineChartDataKey)}`]:
              value,
            [`${lineChartDataKey}-${getScore(recordedData[nextIndex], lineChartDataKey)}`]:
              value,
            [`${lineChartDataKey}`]: value,
            iscore: getScore(recordedData[nextIndex], lineChartDataKey),
            time: dayjs(data.createTimestamp).valueOf(),
          }
        } else if (multiLineChartKey) {
          let chartData: Record<string, string | number | undefined | null> =
            {}
          multiLineChartKey.forEach((key) => {
            let value = valueManipulation(`${data[key]}`, key)
            let obj = {
              [`${key}-${getScore(data, key)}`]: value,
              // [`${key}-${getScore(recordedData[nextIndex], key)}`]: value,
              [`${key}`]: value,
            }
            chartData = { ...chartData, ...obj }
          })
          return {
            ...chartData,
            time: dayjs(data.createTimestamp).valueOf(),
          }
        } else {
          return {
            time: dayjs(data.createTimestamp).valueOf(),
          }
        }
      })
      : []
  }, [lineChartDataKey, multiLineChartKey, recordedData])

  const yAxisDomain = useMemo(() => {
    if (lineChartDataKey) {
      if (lineChartDataKey === 'mews') {
        return [-1, 3]
      }

      if (lineChartDataKey === 'resp') {
        return ['dataMin - 25', 'dataMax + 25']
      }

      return ['dataMin - 2', 'dataMax + 2']
    }

    if (multiLineChartKey) {
      return ['dataMin - 25', 'dataMax + 25']
    }
  }, [lineChartDataKey, multiLineChartKey])

  useEffect(() => {
    const handleBeforePrint = () => {
      const chart = chartRef.current
      if (chart) {
        html2canvas(chart).then((canvas) => {
          chart.innerHTML = ''
          chart.appendChild(canvas)
        })
      }
    }

    window.addEventListener('beforeprint', handleBeforePrint)

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint)
    }
  }, [])

  return (
    <PrintChartContainer ref={chartRef}>
      <PrintChartTitle>
        {getRecordedChartTitle(lineChartDataName, recordedData)}
      </PrintChartTitle>
      <PrintChartWrapper>
        <ResponsiveContainer
          width='100%'
          height='100%'
          minWidth={0}
          minHeight={196}
          maxHeight={undefined}
          aspect={undefined}
        >
          <LineChart
            {...{
              overflow: 'visible',
            }}
            data={chartData}
            margin={{
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid fill={'#FAFAFAFA'} stroke={'#FAFAFAFA'} />
            {chartData.length > 0 && (
              <XAxis
                orientation='top'
                fontSize={10}
                tickCount={10}
                style={{
                  display: lineChartDataKey !== 'mews' ? 'none' : 'block',
                }}
                type='number'
                tickFormatter={(value) =>
                  dayjs(value).format('DD-MMM-YY HH:mm:ss')
                }
                dataKey='time'
                domain={[dayjs(startTime).valueOf(), dayjs(endTime).valueOf()]}
                interval={'preserveStartEnd'}
              />
            )}
            {chartData.length > 0 && (
              <YAxis
                fontSize={12}
                dataKey={lineChartDataKey}
                domain={yAxisDomain}
                padding={{ top: 24, bottom: 24 }}
                tick={{ dx: -15 }}
              />
            )}

            {lineChartDataKey &&
              ['black', 'green', '#fff991', '#ffcc6c', '#ff6d6d'].map(
                (color, index) => {
                  let score = index - 1
                  return (
                    <Line
                      key={color}
                      isAnimationActive={false}
                      name={`${lineChartDataKey}`}
                      type='linear'
                      strokeWidth={3}
                      dataKey={`${lineChartDataKey}-${score}`}
                      stroke={color}
                      dot={false}
                      activeDot={{ r: 0 }}
                    />
                  )
                }
              )}
             {multiLineChartKey &&
            multiLineChartKey.map((key) =>
              ['black', '#cc00dc', '#fff991', '#ffcc6c', '#ff6d6d'].map(
                (color, index) => {
                  let score = index - 1
                  return (
                    <Line
                      isAnimationActive={false}
                      name={`${key}`}
                      type='linear'
                      strokeWidth={2}
                      dataKey={`${key}-${score}`}
                      stroke={(key === 'dia') ? 'blue' : color}
                      dot={true}
                      activeDot={{ r: 8, stroke: 'white', strokeWidth: 2 }}
                    >                                         
                    </Line>
                  )
                }
              )
            )}
          </LineChart>
        </ResponsiveContainer>
      </PrintChartWrapper>
    </PrintChartContainer>
  )
}


function findMinMax(
  arr: PatientRecordedDataType[],
  key: 'spo2' | 'pr' | 'resp' | 'temp' | 'dia' | 'sys' | 'mews'
) {
  if (arr.length === 0) {
    return null
  }

  let minValue = null
  let maxValue = null

  for (let i = 0; i < arr.length; i++) {
    const value = arr[i][key]

    if (value !== null) {
      minValue = value
      maxValue = value
      break
    }
  }

  for (let i = 0; i < arr.length; i++) {
    const value = arr[i][key]

    if (value !== null) {
      minValue = Math.min(minValue!, value)
      maxValue = Math.max(maxValue!, value)
    }
  }

  if (key === 'mews') {
    minValue = minValue && minValue < 0 ? 0 : minValue
    return { min: minValue, max: maxValue }
  }
  if (minValue && maxValue) {
    return { min: minValue, max: maxValue }
  } else {
    return null
  }
}

function getRecordedChartTitle(
  chartName: string,
  chartData: PatientRecordedDataType[] | undefined
) {
  if (!chartData) return <></>

  if (chartName === 'mews') {
    let rangeValues = findMinMax(chartData, chartName)
    if (!rangeValues) {
      return (
        <div>
          <div>HS</div>
          <div>NO DATA</div>
        </div>
      )
    }
    return (
      <div>
        <div>HS</div>
        <div>MAX: {rangeValues.max}</div>
        <div>MIN: {rangeValues.min}</div>
      </div>
    )
  } else if (chartName === 'spo2') {
    let rangeValues = findMinMax(chartData, chartName)
    if (!rangeValues) {
      return (
        <div>
          <div>Spo2</div>
          <div>NO DATA</div>
        </div>
      )
    }
    return (
      <div>
        <div>Spo2</div>
        <div>MAX: {rangeValues.max}%</div>
        <div>MIN: {rangeValues.min}%</div>
      </div>
    )
  } else if (chartName === 'pr') {
    let rangeValues = findMinMax(chartData, chartName)
    if (!rangeValues) {
      return (
        <div>
          <div>PR</div>
          <div>NO DATA</div>
        </div>
      )
    }
    return (
      <div>
        <div>PR</div>
        <div>MAX: {rangeValues.max} bpm</div>
        <div>MIN: {rangeValues.min} bpm</div>
      </div>
    )
  } else if (chartName === 'temp') {
    let rangeValues = findMinMax(chartData, chartName)
    if (!rangeValues) {
      return (
        <div>
          <div>Temp</div>
          <div>NO DATA</div>
        </div>
      )
    }
    return (
      <div>
        <div>Temp</div>
        <div>MAX: {rangeValues.max} °F</div>
        <div>MIN: {rangeValues.min} °F</div>
      </div>
    )
  } else if (chartName === 'resp') {
    let rangeValues = findMinMax(chartData, chartName)
    if (!rangeValues) {
      return (
        <div>
          <div>Resp</div>
          <div>NO DATA</div>
        </div>
      )
    }
    return (
      <div>
        <div>Resp</div>
        <div>MAX: {rangeValues.max} bpm</div>
        <div>MIN: {rangeValues.min} bpm</div>
      </div>
    )
  } else if (chartName === 'bp') {
    let rangeValuesSys = findMinMax(chartData, 'sys')
    let rangeValuesDia = findMinMax(chartData, 'dia')
    if (!rangeValuesSys || !rangeValuesDia) {
      return (
        <div>
          <div>BP</div>
          <div>NO DATA</div>
        </div>
      )
    }
    return (
      <div>
        <div>BP</div>
        <div>
          MAX: {rangeValuesSys.max}/{rangeValuesDia.max}
        </div>
        <div>
          MIN: {rangeValuesSys.min}/{rangeValuesDia.min}
        </div>
      </div>
    )
  }

  return <></>
}

function parseNumber(value: string) {
  if (!value || isNaN(parseInt(value))) return 0
  return parseInt(value)
}

const getScore = (
  data: PatientRecordedDataType,
  key: 'spo2' | 'pr' | 'resp' | 'temp' | 'dia' | 'sys' | 'mews'
) => {
  let score = 0

  if (key === 'dia' || key === 'sys') {
    score = parseNumber(`${data[`bpScore`]}`)
  } else if (key === 'mews') {
    score = parseNumber(`${data[`mews`]}`)
  } else {
    score = parseNumber(`${data[`${key}Score`]}`)
  }

  return score >= 3 ? 3 : score
}

function valueManipulation(
  value: string,
  key: 'spo2' | 'pr' | 'resp' | 'temp' | 'dia' | 'sys' | 'mews'
) {
  let numValue = key === 'temp' ? parseFloat(value) : parseInt(value)
  if (key === 'mews') return numValue < 0 ? null : numValue
  if (!numValue) return undefined
  return numValue
}